import { useParams } from "react-router";
import { MODELS, useModel } from "../../../../api";
import { ShellContent } from "../../../shell/Shell.jsx";
import tokens from "../../../../design-system/tokens.js";
import Layout from "../../../../design-system/components/Layout/Layout.jsx";
import Header from "../../../../design-system/components/Header/Header.jsx";
import { SanityImg } from "../../../../lib/images.jsx";
import { workshopStartStyles } from "../../../admin/workshops/workshops-styles.css.js";
import { TagRow } from "../TagRow/TagRow.jsx";
import BlockContent from "../../../../design-system/components/BlockContent/BlockContent.jsx";
import libraryStyles from "../../library.css.ts";
import cx from "../../../../lib/cx.js";

/**
 * FullLibraryItemWithData is a React functional component designed to render a full library item with its associated data.
 *
 * @param {Object} item - The library item data used for rendering.
 * @param {string} item.imageUrl - The URL of the item's image. If provided, it renders an image with specific styling.
 * @param {Object} item.content - The content object containing textual information for rendering.
 * @param {string} item.content.textRaw - Raw text content rendered using a BlockContent component.
 * @param {string} item.title - The title of the library item displayed in larger, bolded text.
 *
 * @returns {JSX.Element} A structured view of the library item, including its image, tags, title, and content if available.
 */
const FullLibraryItemWithData = ({ item }) => {
  return (
    <ShellContent bg={tokens.color.full.gray800}>
      <Layout
        Header={() => (
          <Header
            title={item.title}
            back
            backPath="../library"
            isDarkMode
            replaceLink
          />
        )}
      >
        {item.imageUrl && (
          <SanityImg
            url={item.imageUrl}
            height={650}
            width={1920}
            fit="crop"
            className={cx(
              workshopStartStyles.coverImg,
              libraryStyles.fullLibraryItemHeaderImage,
            )}
          />
        )}
        <TagRow item={item} />
        <h2
          className={cx(libraryStyles.boxHeadline, libraryStyles.withMarginTop)}
        >
          {item.title}
        </h2>
        {item.content.textRaw && (
          <BlockContent content={item.content.textRaw} textColor="white" />
        )}
      </Layout>
    </ShellContent>
  );
};
/**
 * FullLibraryItem renders a specific library item based on its route id.
 * Loading suspends and a missing item 404s to the nearest route boundary.
 */
const FullLibraryItem = () => {
  const { libraryItemId } = useParams();

  const item = useModel(MODELS.LIBRARY_ITEM(libraryItemId));

  return <FullLibraryItemWithData item={item} />;
};

export default FullLibraryItem;
